import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface CartItem {
  cartItemId: string;
  id: number;
  productName: string;
  price: number;
  quantity: number;
  imageUrl: string;
  option?: any;
  note?: string;
}

type CartItemInput = Omit<CartItem, "cartItemId">;

const generateId = (): string => {
  if (typeof crypto !== "undefined" && (crypto as any).randomUUID) {
    return (crypto as any).randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

export interface CartContext {
  shopId: number | string;
  qrToken: string;
}

export interface CartState {
  items: CartItem[];
  total: number;
  shopId: number | string | null;
  qrToken: string | null;
}

const initialState: CartState = {
  items: [],
  total: 0,
  shopId: null,
  qrToken: null,
};

const clearItems = (state: CartState) => {
  state.items = [];
  state.total = 0;
};

const isSameContextValue = (
  left: number | string | null,
  right: number | string | null
) => {
  if (left === null || right === null) return left === right;
  return String(left) === String(right);
};

const cartSlice = createSlice({
  name: "Cart",
  initialState,
  reducers: {
    setCartContext(state, action: PayloadAction<CartContext>) {
      const { shopId, qrToken } = action.payload;
      const isSameShop = isSameContextValue(state.shopId, shopId);
      const isSameQrToken = isSameContextValue(state.qrToken, qrToken);

      if (!isSameShop || !isSameQrToken) {
        clearItems(state);
      }

      state.shopId = shopId;
      state.qrToken = qrToken;
    },
    addItem(state, action: PayloadAction<CartItemInput>) {
      const incoming = action.payload;
      const existing = state.items.find(
        (i) =>
          i.id === incoming.id &&
          JSON.stringify(i.option) === JSON.stringify(incoming.option) &&
          i.note === incoming.note
      );
      if (existing) {
        existing.quantity += incoming.quantity;
      } else {
        state.items.push({ ...incoming, cartItemId: generateId() });
      }
      state.total = state.items.reduce(
        (sum, i) => sum + i.price * i.quantity,
        0
      );
    },
    removeItem(state, action: PayloadAction<string>) {
      state.items = state.items.filter((i) => i.cartItemId !== action.payload);
      state.total = state.items.reduce(
        (sum, i) => sum + i.price * i.quantity,
        0
      );
    },
    updateQuantity(
      state,
      action: PayloadAction<{ cartItemId: string; quantity: number }>
    ) {
      const { cartItemId, quantity } = action.payload;
      const item = state.items.find((i) => i.cartItemId === cartItemId);
      if (item) {
        item.quantity = quantity;
      }
      state.total = state.items.reduce(
        (sum, i) => sum + i.price * i.quantity,
        0
      );
    },
    updateItem(
      state,
      action: PayloadAction<{ index: number; item: CartItem }>
    ) {
      const { index, item } = action.payload;
      if (state.items[index]) {
        state.items[index] = item;
      }
      state.total = state.items.reduce(
        (sum, i) => sum + i.price * i.quantity,
        0
      );
    },
    clearCart(state) {
      clearItems(state);
    },
  },
});

export const {
  addItem,
  removeItem,
  updateQuantity,
  updateItem,
  clearCart,
  setCartContext,
} = cartSlice.actions;
export default cartSlice.reducer;
